import { z } from 'zod';
import { MAX_MINOR_UNITS, CalendarDateSchema } from './common.js';
// =============================================================================
// Credit Note
// =============================================================================
export const CreditNoteStatusSchema = z.enum(['ISSUED', 'VOIDED']);
export const CreditNoteLineItemSchema = z.object({
    id: z.string(),
    creditNoteId: z.string(),
    invoiceLineItemId: z.string().nullable(),
    description: z.string(),
    totalAmountMinorUnits: z.number().int(),
    vatAmountMinorUnits: z.number().int().nullable().optional(),
    vatRateBasisPoints: z.number().int(),
    ledgerAccountId: z.string().nullable(),
    isDisbursement: z.boolean(),
    createdAt: z.string(),
});
export const CreditNoteSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    clientId: z.string(),
    invoiceId: z.string(),
    creditNoteNumber: z.string().nullable(),
    date: z.string(),
    status: CreditNoteStatusSchema,
    currency: z.string(),
    subtotalMinorUnits: z.number().int(),
    taxAmountMinorUnits: z.number().int(),
    totalMinorUnits: z.number().int(),
    notes: z.string().nullable(),
    pdfStoragePath: z.string().nullable(),
    ledgerEntrySetId: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreditNoteWithLineItemsSchema = CreditNoteSchema.extend({
    lineItems: z.array(CreditNoteLineItemSchema),
});
// =============================================================================
// Credit Note Inputs
// =============================================================================
/**
 * A line of a partial or custom credit note. A line either names the invoice
 * line it credits (`invoiceLineItemId`, a linked line) or stands alone (an
 * unlinked line). Presentational fields (units, price, date, performer) are
 * not accepted and are written as null.
 *
 * - `totalAmountMinorUnits`: VAT-exclusive net in minor units, capped at the
 *   PostgreSQL Int column maximum.
 * - `invoiceLineItemId`: optional. When set, the line must be a non-discount
 *   line of the credited invoice, named at most once per request. Its account,
 *   VAT rate and disbursement flag come from that invoice line, and any of
 *   those supplied with a different value is a 400. The amount must be above 0
 *   and at most what is left to credit on the line (`invoiceLineCreditPositions`).
 *   VAT is computed by the server (`linkedCreditVatMinorUnits`); a supplied
 *   non-null VAT that differs is a 400. Linking is refused on an invoice that
 *   carries a legacy discount row (negative, no exact VAT): use the
 *   full-invoice credit note or unlinked lines instead.
 * - `vatAmountMinorUnits`: exact VAT in minor units. Unlinked lines: omitted or
 *   null derives VAT from the rate.
 * - `vatRateBasisPoints`: unlinked lines only, optional. When omitted the rate
 *   is inherited from the credited invoice's lines (not the account's VAT
 *   registration), which works only when they all share one rate; otherwise
 *   the request is a 400. An explicit value, including 0, always wins.
 * - `isDisbursement`: places the line under Fees or Disbursements, and on an
 *   unlinked line with no account it picks the default (see `ledgerAccountId`).
 *   Deliberately has no schema default, so a linked line can tell "not sent"
 *   from "sent false"; unlinked lines default to false.
 * - `ledgerAccountId`: unlinked lines on an active ledger take a supplied
 *   account only if it is on that ledger, not archived, and passes
 *   `isPermittedIncomeTarget` (an income account or DEFAULT_DISBURSEMENT);
 *   omitted or null takes the invoice line default for a line with no
 *   performer: the DEFAULT_RECOVERY account for a disbursement when the ledger
 *   has one, else DEFAULT_FEE. With no active ledger nothing is posted and null is
 *   accepted. Linked lines and the full-invoice clone skip the allowlist and
 *   mirror the invoice line's posted account, so a legacy invoice on a
 *   non-allowlisted account stays creditable; both are still refused before
 *   any write when that account cannot resolve (null on a ledgered account,
 *   archived, or on another ledger).
 */
export const CreditNoteLineItemInputSchema = z.object({
    description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less'),
    // VAT-exclusive net. Cap at PostgreSQL Int max, matching the column type.
    totalAmountMinorUnits: z.number().int('Total amount must be an integer').min(0, 'Total amount may not be negative').max(MAX_MINOR_UNITS, 'Total amount exceeds the maximum allowed value'),
    vatAmountMinorUnits: z.number().int().min(0).max(MAX_MINOR_UNITS).nullable().optional(),
    vatRateBasisPoints: z.number().int('VAT rate must be an integer').min(0).max(10000).optional(),
    invoiceLineItemId: z.string().uuid().optional(),
    isDisbursement: z.boolean().optional(),
    ledgerAccountId: z.string().uuid().nullable().optional(),
});
export const CreateCreditNoteInputSchema = z.object({
    /** Client-generated idempotency key (UUID). Re-submitting with the same key returns the existing credit note. */
    idempotencyKey: z.string().min(1, 'Idempotency key is required'),
    /**
     * Optional accounting date for the credit note (ISO 8601 YYYY-MM-DD or date-time).
     * When omitted, the service defaults to today's date. Callers creating historical
     * credit notes (e.g. backdating to match the original invoice period) must supply
     * this field explicitly. Accepts either a plain date (`2026-01-31`) or a full
     * RFC 3339 date-time; the domain layer normalises both to UTC midnight.
     */
    date: CalendarDateSchema.optional(),
    /**
     * Optional lines for a partial or custom-amount credit note; at least one when
     * present. Totals are summed from the written lines, and a linked line keeps
     * its `invoiceLineItemId` (see {@link CreditNoteLineItemInputSchema}). When
     * absent, the whole invoice is credited by cloning its lines, net of
     * allocated discounts.
     */
    lineItems: z.array(CreditNoteLineItemInputSchema).min(1, 'At least one line item is required').optional(),
    /**
     * Optional brief explanation of why the credit was issued, saved on the
     * credit note and printed on its PDF. Trimmed; omitted or blank is stored as
     * no reason (null). Applies to both full and custom-line credit notes.
     */
    notes: z.string().trim().max(2000, 'Reason must be 2000 characters or less').optional(),
});
